import styled, { keyframes } from 'styled-components';

// --- Animations ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-20px);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
  70% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
  100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
`;

export const FadeIn = styled.div`
  animation: ${fadeIn} 0.5s ease-out;
  margin: 0 auto;
  width: 100%;
  display: flex;
  justify-content: center;
`;

export const SampleFadeIn = styled.div`
  animation: ${fadeIn} 0.5s ease-out;
  margin: 0 auto;
  width: 100%;
  display: inline-block;
`;

export const PageContainer = styled.div<{ $isActive?: boolean }>`
  height: 100vh;
  background-color: #f0f2f5;
  display: flex;
  justify-content: center;
  align-items: ${props => props.$isActive ? 'stretch' : 'center'};
  padding: ${props => props.$isActive ? '0' : '20px'};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #333;
  overflow: hidden;
`;

// --- NEW SPLIT LAYOUT COMPONENTS ---
export const SplitCard = styled.div`
  display: flex;
  flex-direction: row;
  background: white;
  width: 100%;
  max-width: 1800px;
  height: 95vh;
  // max-height: 1000px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
    max-height: none;
  }
`;

export const Sidebar = styled.div<{ color?: string, cover?: string }>`
  flex: 0 0 35%;
background: ${props => props.cover
    ? `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${props.cover}) center/cover no-repeat`
    : props.color
      ? `linear-gradient(135deg, ${props.color}, ${props.color}dd)`
      : 'linear-gradient(135deg, #1e293b, #0f172a)'
  };
  color: white;
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(white 1px, transparent 1px);
    background-size: 30px 30px;
    opacity: 0.1;
  }
`;

export const SidebarContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;

  h1 {
    font-size: 1.8rem;
    margin: 0;
    font-weight: 700;
    line-height: 1.2;
  }
`;

export const JobPill = styled.div`
  background: rgba(255, 255, 255, 0.15);
  padding: 6px 16px;
  border-radius: 50px;
  font-size: 0.9rem;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255,255,255,0.2);
`;

export const CompanyLogoBig = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
  background: white;
  border-radius: 20px;
  padding: 10px;
  box-shadow: 0 8px 20px rgba(0,0,0,0.2);
`;

export const MainContent = styled.div`
  flex: 1;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const ScrollableArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 3rem;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
`;

export const StickyFooter = styled.div`
  padding: 1.5rem 3rem;
  background: white;
  border-top: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 10;
`;

// --- TEST DETAILS STYLING ---
export const DetailTitle = styled.h2`
  font-size: 2rem;
  margin: 0 0 1rem 0;
  color: #1e293b;
`;

export const DetailText = styled.div`
  color: #64748b;
  line-height: 1.6;
  font-size: 1rem;
  margin-bottom: 2rem;
  
  p { margin-bottom: 0.75rem; }
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2.5rem;
`;

export const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

export const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
`;

export const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StatLabel = styled.span`
  font-size: 0.75rem;
  color: #94a3b8;
  text-transform: uppercase;
  font-weight: 600;
`;

export const StatValue = styled.span`
  font-size: 1.1rem;
  color: #0f172a;
  font-weight: 600;
`;

export const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const ExampleSection = styled.div`
  margin-top: 2rem;
  
  h3 {
    font-size: 0.9rem;
    text-transform: uppercase;
    color: #94a3b8;
    letter-spacing: 0.05em;
    margin-bottom: 1rem;
  }
`;

export const ExampleCard = styled.div<{ $borderColor?: string }>`
  background: white;
  border: 1px solid #e2e8f0;
  border-left: 4px solid ${props => props.$borderColor || '#3b82f6'};
  padding: 1.5rem;
  margin-bottom: 1rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
    border-left-color: #2563eb;
    background: #f8fafc;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
`;

export const QuestionNumber = styled.span`
  font-weight: 700;
  color: #3b82f6;
  margin-right: 8px;
`;

export const QuestionText = styled.span`
  color: #334155;
  font-weight: 500;
  display: inline;
`;

export const AnswersGrid = styled.div`
  margin-top: 1rem;
  padding-left: 1rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`;

export const AnswerOption = styled.div`
  font-size: 0.85rem;
  color: #64748b;
  background: #fff;
  border: 1px solid #f1f5f9;
  padding: 4px 8px;
  border-radius: 4px;
`;

// --- ACTIVE TEST COMPONENTS ---
export const FocusContainer = styled.div`
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const TopBar = styled.div`
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2rem;
  border-bottom: 1px solid #f1f1f1;
  
  .brand {
    font-weight: 600;
    color: #333;
  }
`;

export const TimerBadge = styled.div<{ $isUrgent?: boolean }>`
  background: ${props => props.$isUrgent ? '#fee2e2' : '#f1f5f9'};
  color: ${props => props.$isUrgent ? '#ef4444' : '#475569'};
  padding: 6px 14px;
  border-radius: 20px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
`;

export const CustomTimerBadge = styled.div<{ $isUrgent?: boolean }>`
  display: inline-flex;
  justify-content: end;
  align-items: center;
  gap: 10px;
  padding: 6px 14px;
  // border-radius: 8px; // Sharp but rounded for a modern "pro" look
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 600;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  
  /* Background & Text Color */
  background: ${props => props.$isUrgent
    ? '#fef2f2' // Solid light red background for visibility
    : '#ffffff'};
  color: ${props => props.$isUrgent ? '#b91c1c' : '#475569'};
  
  /* High-Definition Borders */
  border: 1.5px solid ${props => props.$isUrgent
    ? '#fecaca'
    : '#e2e8f0'};

  /* Animation & Effects */
  transition: all 0.2s ease-in-out;
  animation: ${props => props.$isUrgent ? pulse : 'none'} 2s infinite;

  svg {
    opacity: ${props => props.$isUrgent ? 1 : 0.7};
    stroke: ${props => props.$isUrgent ? '#dc2626' : '#64748b'};
  }

  &:hover {
    border-color: ${props => props.$isUrgent ? '#ef4444' : '#cbd5e1'};
    transform: translateY(-1px);
  }
`;

export const ProgressBarContainer = styled.div`
  width: 100%;
  height: 4px;
  background: #f1f1f1;
`;

export const ProgressFill = styled.div<{ $percentage: number }>`
  height: 100%;
  width: ${props => props.$percentage}%;
  background: #3b82f6;
  transition: width 0.5s ease;
`;

export const ContentBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
`;

export const QuestionDisplay = styled.div`
  font-size: 1.125rem;
  line-height: 1.4;
  color: #111;
  margin-bottom: 2rem;
  
  img {
    max-width: 100%;
    border-radius: 8px;
    margin: 1rem 0;
  }
`;

export const AnswerSection = styled.div`
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  
  &:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

export const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  border: none;
  background: transparent;
  font-size: 1.1rem;
  font-family: inherit;
  resize: none;
  outline: none;
  color: #333;
  
  &::placeholder {
    color: #cbd5e1;
  }
`;

// --- BUTTONS ---
export const PrimaryButton = styled.button`
  background: #111;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 5px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const SecondaryButton = styled(PrimaryButton)`
  background: #e2e8f0;
  color: #334155;
  
  &:hover:not(:disabled) {
    background: #cbd5e1;
  }
`;

export const GradientButton = styled(PrimaryButton)`
  background: linear-gradient(135deg, #22c993 0%, #1eb685 100%);
  box-shadow: 0 10px 20px rgba(34, 201, 147, 0.25);
  
  &:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 15px 30px rgba(34, 201, 147, 0.35);
  }
`;

// --- MODALS & OVERLAYS ---
export const WarningModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(4px);
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const WarningCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  max-width: 400px;
  text-align: center;
  box-shadow: 0 20px 60px -10px rgba(0, 0, 0, 0.1);
  
  h3 {
    color: #ef4444;
    margin-top: 0;
  }
`;

// --- LEGACY COMPONENTS (For backward compatibility) ---
export const TestDetailContainer = styled(PageContainer)`
  padding: 40px 20px;
  min-height: 100vh;
  align-items: flex-start;
`;

export const CompanyHeader = styled.div<{ $isCompact?: boolean; $color?: string }>`
  background: linear-gradient(135deg, ${props => props.$color || '#22c993'} 0%, ${props => props.$color || '#1eb685'} 100%);
  border-radius: 16px;
  padding: ${props => props.$isCompact ? '24px 32px' : '40px'};
  margin-bottom: 32px;
  color: white;
  box-shadow: 0 10px 30px rgba(34, 201, 147, 0.3);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
`;

export const CompanyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 16px;
`;

export const CompanyLogo = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

export const CompanyName = styled.h3`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: white;
`;

export const TestCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
`;

export const TestTitle = styled.h1`
  font-size: 27px;
  font-weight: 700;
  color: #18213c;
  margin: 0 0 16px 0;
  letter-spacing: -0.5px;
`;

export const TestDescription = styled.div`
  font-size: 16px;
  line-height: 1.6;
  color: #5a6c7d;
  margin-bottom: 24px;

  p {
    margin: 0 0 12px 0;
  }
`;

export const ActionSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 16px;
  margin-top: 40px;
`;

export const StartButton = styled(GradientButton)`
  padding: 18px 48px;
  font-size: 18px;
`;

export const WarningText = styled.p`
  font-size: 14px;
  color: #f59e0b;
  text-align: center;
  margin: 0;
  font-weight: 500;
`;

export const TestActiveSection = styled.div`
  animation: ${slideUp} 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  margin-top: 32px;
`;

export const TimerProgressSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  animation: ${fadeIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
`;

export const TimerDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #22c993;
  
  svg {
    width: 28px;
    height: 28px;
  }
`;

export const ProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const ProgressText = styled.span`
  font-size: 0.875rem;
  color: #4a5568;
  font-weight: 600;
`;

export const ProgressBarTrack = styled.div`
  width: 100%;
  height: 10px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
`;

export const ProgressBarFill = styled.div<{ $percentage: number }>`
  height: 100%;
  width: ${props => props.$percentage}%;
  background: linear-gradient(90deg, #22c993 0%, #1eb685 100%);
  border-radius: 999px;
  transition: width 0.4s cubic-bezier(0.16, 1, 0.3, 1);
`;

export const QuestionCard = styled.div`
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(34, 201, 147, 0.1);
  padding: 48px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  animation: ${scaleIn} 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.2s backwards;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, #22c993 0%, #1eb685 100%);
  }

  &:hover {
    box-shadow: 0 25px 70px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
`;

export const QuestionTextContent = styled.div`
  font-size: 1.25rem;
  font-weight: 400;
  color: #1a202c;
  margin-bottom: 2.5rem;
  
  p {
    margin: 0;
  }
`;

export const AnswerInput = styled(StyledTextArea)`
  min-height: 180px;
  padding: 1.5rem;
  border: 2px solid #e2e8f0;
  font-size: 1.125rem;
  line-height: 1.6;
  background: #f8fafc;
  
  &:focus {
    outline: none;
    border-color: #22c993;
    background: white;
    box-shadow: 0 0 0 4px rgba(34, 201, 147, 0.15);
  }
`;

export const SubmitButton = styled(GradientButton)`
  padding: 1.25rem 3.5rem;
  font-size: 1.125rem;
  letter-spacing: 0.02em;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

export const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid #22c993;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

export const ErrorMessage = styled.div`
  background: #fee2e2;
  border: 1px solid #fca5a5;
  border-radius: 12px;
  padding: 16px;
  color: #dc2626;
  font-size: 14px;
  margin-bottom: 24px;
`;

export const Card = styled.div`
  background: white;
  width: 100%;
  max-width: 600px;
  border-radius: 24px;
  box-shadow: 0 20px 60px -10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  background: white;
  
  /* Ensure it doesn't touch edges on mobile */
  margin: 1rem;
`;

export const ExampleQcmOption = styled.button`
  padding: 12.5px;
  border-radius: 25px;
  border: 1px solid #1eb685;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  &:hover {
    background-color: #1eb685;
    color: white;
    scale: 1.02;
  }
`;

export const QcmOption = styled.button`
  font-size: 1.25rem;
  padding: 20px;
  border-radius: 25px;
  border: 1px solid #1eb685;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  &:hover {
    background-color: #1eb685;
    color: white;
    scale: 1.02;
  }
`;

export const SpinWrapper = styled.div`
  display: inline-block;
  animation: ${spin} 1s linear infinite;
  line-height: 0;
`;

// Utility class for raw SVG if needed
export const GlobalStyles = styled.div`
  .animate-spin {
    animation: ${spin} 1s linear infinite;
  }
`;

export const Grid2 = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12.5px;
  margin-top: 50px;
  justify-content: center;
`;

